import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  List,
  ListItem,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import type {
  TournamentEventResponse,
  EventEligibilityVerdict,
  TournamentPlayerEntryResponse,
  DrawResponse,
  TournamentResponse,
} from "@bw/shared/types/generated";
import { useMyProfile } from "../../../common/query/useMyProfile";
import { useTournamentEligibility } from "../hooks/useTournamentEligibility";
import { useTournamentRegistrations } from "../hooks/useTournamentRegistrations";
import { useTournamentDraws } from "../hooks/useTournamentDraws";
import { useTournamentEventCatalog } from "../hooks/useTournamentEventCatalog";
import { useWithdrawEntry } from "../hooks/useWithdrawEntry";
import { humanizeEntryStatus } from "../utils/entryStatus";
import { TournamentEventRow } from "./TournamentEventRow";
import { TournamentRegisterDialog } from "./TournamentRegisterDialog";
import { TournamentWithdrawDialog } from "./TournamentWithdrawDialog";
import { PartnerFinderDialog } from "./PartnerFinderDialog";
import { visuallyHidden } from "../../../common/visuallyHidden";

type TournamentLegalDocument = NonNullable<
  TournamentResponse["legal_documents"]
>[number];

/**
 * TournamentEventList (EPIC 1.2) — the multi-event register surface that replaces
 * the single-select TournamentRegisterPanel: ONE row per tournament event, each
 * showing the signed-in player's per-event eligibility + live status, so a player
 * can register for SEVERAL events (MS + MD + XD…) independently.
 *
 * It joins three SOURCES per event (a THIN layer — renders backend facts, never
 * recomputes):
 *   - `events`  — the catalog (passed in; the page already fetched it, deduped).
 *   - useTournamentEligibility(tournamentId) — the BACKEND verdicts, matched to a
 *     row by event_id. Ineligible → greyed + reasons[0].message verbatim.
 *   - useTournamentRegistrations(tournamentId) — the caller's LIVE entries. CRUCIAL:
 *     TournamentPlayerEntryResponse has NO event_id — it carries `draw_id`. Each entry is
 *     resolved to its owning event by joining draw_id → DrawResponse.event_id
 *     (useTournamentDraws). The join sets each row's `myEntry`; an entry for event A
 *     never bleeds onto B.
 *
 * Per-event actions reuse the existing money/checkout/withdraw flow:
 *   - onRegister(event) → TournamentRegisterDialog for THAT event (free → waiver-
 *     gate→register; paid → the Stripe checkout), using the player-specific quote
 *     from eligibility. Needs `facilityId` (payments are facility-scoped); when
 *     absent the surface still renders read-only rows.
 *   - onWithdraw(entry) → TournamentWithdrawDialog (the #1402 refund path), reusing
 *     the existing dialog + useWithdrawEntry.
 *
 * On a successful register/withdraw the mutations invalidate the tournament-scoped
 * eligibility + registrations queries, so each row re-reads the server's truth —
 * never an optimistic local flip.
 */
export function TournamentEventList(
  props: React.ComponentProps<typeof TournamentEventListBody>,
) {
  const { user } = useAuth();
  const [params] = useSearchParams();
  return (
    <TournamentEventListBody
      key={`${user?.uid ?? "anonymous"}:${props.tournamentId}:${params.get("entry_id") ?? "all"}`}
      {...props}
    />
  );
}

function TournamentEventListBody({
  tournamentId,
  events,
  facilityId,
  refundPolicySummary,
  legalDocuments = [],
  legalNotes = null,
  requiresForeignDeclaration = false,
  registrationOpen = true,
  eventsLoading = false,
  eventsError = false,
}: {
  tournamentId: string;
  events: TournamentEventResponse[];
  /**
   * Whether registration is OPEN (the page's lifecycle.registration_state ===
   * "open"). Threaded to each row: when FALSE, a not-entered row renders READ-ONLY
   * (display facts only, no Register/Enter-solo) — the browse behavior the deleted
   * read-only catalog provided for a closed/in-progress tournament. An active own-
   * entry always keeps its Withdraw regardless. Default TRUE so the prop-driven
   * unit tests keep the actionable affordance.
   */
  registrationOpen?: boolean;
  /**
   * The tournament's facility, needed by the per-event paid checkout (payments are
   * facility-scoped). Optional so the list can render read-only rows without it
   * (and so the unit test can render with just tournamentId + events).
   */
  facilityId?: string;
  /** The refund POLICY summary, shown DISPLAY-ONLY in the withdraw confirm. */
  refundPolicySummary?: string | null;
  /** Participant-scoped requirements from the tournament detail response. */
  legalDocuments?: TournamentLegalDocument[];
  legalNotes?: string | null;
  requiresForeignDeclaration?: boolean;
  /**
   * The events-FETCH state, threaded from the page's `useTournamentEvents` query
   * (EPIC2 slice (b) merge — the page owns the single events fetch; this list is
   * the ONE Events surface, so it owns the events-fetch error + empty UX the
   * deleted catalog used to own). `eventsError` → an ISOLATED role="alert" (the
   * events failure must NOT blank the header/schedule, which live above this list);
   * `eventsLoading` → the events-fetch loading note. Default false so the existing
   * prop-driven unit tests (which pass `events` directly and don't fetch) keep
   * their loading gate driven by the three eligibility/registration/draws hooks.
   */
  eventsLoading?: boolean;
  eventsError?: boolean;
}) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const selectedEntryId = params.get("entry_id");
  const myUid = user?.uid ?? null;

  // Is the signed-in registrant a MINOR (#1684)? The register/solo dialog must
  // collect a parent/guardian name + email for an under-18 player (the backend
  // 400s a minor's entry without them). The signal is the backend's OWN
  // `is_minor` profile flag — never client-side age math (the FE is a thin
  // presentation layer; age/consent are backend-owned). The read is gated on
  // `myUid` so an anonymous browser never fires the authed /users/profile call.
  const profileQ = useMyProfile(Boolean(myUid));
  const isMinor = profileQ.data?.is_minor === true;

  const eligibilityQ = useTournamentEligibility(tournamentId);
  const registrationsQ = useTournamentRegistrations(tournamentId);
  const drawsQ = useTournamentDraws(tournamentId);
  // #2691 display-only catalog; a failed read leaves the rows as they were.
  const catalog = useTournamentEventCatalog(tournamentId).data;
  const catalogByEventId = new Map(catalog?.items.map((item) => [item.event_id, item]) ?? []);

  // Which event the player is registering for / withdrawing from (dialog state).
  // `registerMode` picks the dialog mode — "solo" for a doubles/mixed Enter-solo
  // action, else the normal "register" flow (SLICE 1.4-a).
  const [registerEventId, setRegisterEventId] = useState<string | null>(null);
  const [registerMode, setRegisterMode] = useState<"register" | "solo">(
    "register",
  );
  const [withdrawEntry, setWithdrawEntry] =
    useState<TournamentPlayerEntryResponse | null>(null);
  // The own `partner_wanted` entry whose partner finder is open (SLICE 1.4-a;
  // dialog wired in 1.4-c). Set by the row's "Find a partner" → mounts the
  // PartnerFinderDialog scoped to that entry's draw.
  const [finderDraw, setFinderDraw] =
    useState<TournamentPlayerEntryResponse | null>(null);

  const withdraw = useWithdrawEntry(tournamentId);

  // verdict lookup by event_id (the verdict carries event_id).
  const verdictByEventId = useMemo(() => {
    const map = new Map<string, EventEligibilityVerdict>();
    for (const v of eligibilityQ.data ?? []) {
      if (v.event_id) map.set(v.event_id, v);
    }
    return map;
  }, [eligibilityQ.data]);

  const eligibilityQueryFailed = Boolean(user && eligibilityQ.isError);
  const someEligibilityUnavailable = Boolean(
    user &&
      !eligibilityQ.isError &&
      events.some((event) => !verdictByEventId.has(event.event_id)),
  );

  function signInToRegister() {
    const returnUrl = location.pathname + location.search;
    navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
  }

  // draw_id → event_id, the join table for resolving an entry to its event.
  const eventIdByDrawId = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of (drawsQ.data ?? []) as DrawResponse[]) {
      if (d.draw_id && d.event_id) map.set(d.draw_id, d.event_id);
    }
    return map;
  }, [drawsQ.data]);

  // The set of event_ids actually rendered as rows (the catalog). An entry only
  // gets a normal row if its resolved event is one of these.
  const visibleEventIds = useMemo(
    () => new Set(events.map((e) => e.event_id)),
    [events],
  );

  // The caller's LIVE entry per event, resolved by the draw→event join. When the
  // player has multiple entries across one event's draws, prefer an ACTIVE one over
  // a withdrawn/rejected one so a re-register row is never shown for an event the
  // player actually still holds.
  //
  // ORPHANS (P1 money-adjacent): an entry whose draw_id does NOT resolve to a
  // VISIBLE event — the draws/registrations fetches disagree, or the event isn't in
  // the catalog — must NOT be silently dropped. A dropped ACTIVE (registered/
  // waitlisted/pending_review) entry is a paid place the player can't see or
  // cancel. Such active orphans are collected separately and surfaced (below) with
  // a Withdraw control, using the hydrated names already on the entry (no extra
  // fetch). Withdrawn/rejected/cancelled orphans stay hidden (correctly inert).
  const { entryByEventId, orphanActiveEntries } = useMemo(() => {
    const byEvent = new Map<string, TournamentPlayerEntryResponse>();
    const orphans: TournamentPlayerEntryResponse[] = [];
    for (const e of (registrationsQ.data ??
      []) as TournamentPlayerEntryResponse[]) {
      const eventId = eventIdByDrawId.get(e.draw_id);
      if (eventId && visibleEventIds.has(eventId)) {
        const existing = byEvent.get(eventId);
        if (!existing || preferEntry(e, existing)) {
          byEvent.set(eventId, e);
        }
      } else if (!INACTIVE_ENTRY_STATUSES.has(e.status)) {
        // Un-joinable AND still active → surface it as a fallback so the held
        // place stays withdrawable. (Inactive orphans are intentionally dropped.)
        orphans.push(e);
      }
    }
    return { entryByEventId: byEvent, orphanActiveEntries: orphans };
  }, [registrationsQ.data, eventIdByDrawId, visibleEventIds]);

  // The draws for a given event (the dialog needs them for its draw choice).
  const drawsForEvent = useMemo(() => {
    const map = new Map<string, DrawResponse[]>();
    for (const d of (drawsQ.data ?? []) as DrawResponse[]) {
      if (!d.event_id) continue;
      const list = map.get(d.event_id) ?? [];
      list.push(d);
      map.set(d.event_id, list);
    }
    return map;
  }, [drawsQ.data]);

  // Per-event SOLO info (SLICE 1.4-a): an event is partner-requiring when any of
  // its draws has requires_partner; `soloDrawId` is that draw (the one entered
  // solo). Backend-owned fact resolved from the draws join — the row uses it to
  // route the not-entered action to "Enter solo" instead of "Register".
  const soloInfoByEventId = useMemo(() => {
    const map = new Map<
      string,
      { requiresPartner: boolean; soloDrawId?: string }
    >();
    for (const event of events) {
      const partnerDraw = (drawsForEvent.get(event.event_id) ?? []).find(
        (d) => d.requires_partner === true,
      );
      map.set(event.event_id, {
        requiresPartner: partnerDraw != null,
        soloDrawId: partnerDraw?.draw_id,
      });
    }
    return map;
  }, [events, drawsForEvent]);

  // Loading gate: while ANY of the three reads — OR the page's events fetch — is
  // pending, show a single honest loading indicator and NO actionable rows; the
  // register actions must not flash before the eligibility verdicts arrive (a row
  // could wrongly look actionable). `eventsLoading` is the page's events query
  // (slice (b) merge); default false keeps the prop-only unit tests unaffected.
  const isLoading =
    eventsLoading ||
    eligibilityQ.isLoading ||
    registrationsQ.isLoading ||
    drawsQ.isLoading;

  const registerEvent = registerEventId
    ? (events.find((e) => e.event_id === registerEventId) ?? null)
    : null;

  function handleWithdrawConfirm() {
    if (!withdrawEntry || withdraw.isPending) return;
    withdraw.mutate(
      { entryId: withdrawEntry.entry_id },
      {
        onSuccess: () => {
          setWithdrawEntry(null);
        },
      },
    );
  }

  function handleWithdrawClose() {
    if (withdraw.isPending) return;
    setWithdrawEntry(null);
  }

  // The single Events surface's accessible name + heading are CONTEXT-AWARE: when
  // registration is OPEN it is the actionable "Events open for entry" (the name the
  // merged-events RED pins for the surviving region); when CLOSED it is a neutral
  // browse catalog ("Tournament events") — honest copy, since a closed tournament's
  // events are NOT open for entry. Both names are DISTINCT from the deleted
  // catalog's old `aria-label="Events"`, so that region stays gone either way.
  const regionLabel = registrationOpen
    ? "Events open for entry"
    : "Tournament events";
  const heading = registrationOpen ? "Events open for entry" : "Events";
  const selectedEntry =
    user && !registrationsQ.isError
      ? registrationsQ.data?.find(
          (entry) =>
            entry.entry_id === selectedEntryId &&
            entry.tournament_id === tournamentId,
        )
      : undefined;
  const selectedEventId = selectedEntry
    ? eventIdByDrawId.get(selectedEntry.draw_id)
    : undefined;
  const selectedActive = Boolean(
    selectedEntry && !INACTIVE_ENTRY_STATUSES.has(selectedEntry.status),
  );
  const selectedJoined = Boolean(
    selectedEventId && visibleEventIds.has(selectedEventId),
  );
  const showEventRows =
    !selectedEntryId ||
    Boolean(selectedActive && selectedJoined && !drawsQ.isError);
  const visibleEvents = selectedEntryId
    ? events.filter((event) => event.event_id === selectedEventId)
    : events;
  const visibleOrphans = selectedEntryId
    ? orphanActiveEntries.filter(
        (entry) => entry.entry_id === selectedEntry?.entry_id,
      )
    : orphanActiveEntries;

  return (
    <Box component="section" aria-label={regionLabel}>
      <Typography component="h2" sx={visuallyHidden}>
        {heading}
      </Typography>
      {catalog && !selectedEntryId ? (
        <Box data-testid="event-catalog-header" sx={{ mb: 1.5 }}>
          {catalog.closes_line ? (
            <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem" }}>{catalog.closes_line}</Typography>
          ) : null}
          <Typography color="text.secondary" sx={{ fontSize: "0.875rem" }}>
            {[catalog.fee_line, catalog.max_events_line].filter(Boolean).join(" · ")}
          </Typography>
        </Box>
      ) : null}

      {selectedEntryId ? (
        <Stack spacing={1.5} sx={{ mb: 2, alignItems: "flex-start" }}>
          <Typography component="h3" variant="h6">
            Selected entry
          </Typography>
          {!user ? (
            <Button
              component={RouterLink}
              to={`/login?returnUrl=${encodeURIComponent(location.pathname + location.search)}`}
            >
              Sign in to view your entry
            </Button>
          ) : registrationsQ.isLoading || drawsQ.isLoading ? (
            <Typography role="status">Loading your entry…</Typography>
          ) : registrationsQ.isError || drawsQ.isError ? (
            <Alert
              severity="error"
              action={
                <Button
                  onClick={() => {
                    void registrationsQ.refetch();
                    void drawsQ.refetch();
                  }}
                >
                  Retry
                </Button>
              }
            >
              Could not load your entry.
            </Alert>
          ) : selectedEntry ? (
            <>
              <Typography>
                {selectedEntry.event_name ??
                  selectedEntry.draw_name ??
                  "Tournament entry"}
              </Typography>
              <Chip
                role="status"
                label={humanizeEntryStatus(selectedEntry.status)}
              />
              {!selectedJoined ? (
                <Typography color="text.secondary">
                  Selected entry’s event is unavailable.
                </Typography>
              ) : null}
            </>
          ) : (
            <Alert severity="info">Selected entry is unavailable.</Alert>
          )}
          <Button
            onClick={() => {
              const next = new URLSearchParams(params);
              next.delete("entry_id");
              setParams(next);
            }}
          >
            All events
          </Button>
        </Stack>
      ) : null}
      {!showEventRows ? null : eventsError ? (
        // The page's events fetch REJECTED (slice (b) merge — this list owns the
        // events-fetch error the deleted catalog used to). An HONEST, ISOLATED
        // alert: it lives inside this list region only, so the header/schedule
        // above (and the Draws tab) are unaffected. NOT the empty "no events"
        // state — a failed fetch must never read as "there are none".
        <Alert severity="error" role="alert">
          We couldn&apos;t load the events. Please try again.
        </Alert>
      ) : isLoading ? (
        <Typography color="text.secondary" role="status">
          Loading events…
        </Typography>
      ) : events.length === 0 ? (
        // Honest empty — phrased by context: when registration is OPEN the player
        // is looking to enter ("No events open to register for yet."); when CLOSED
        // (browse) the list is just a catalog with nothing listed ("Events to be
        // announced."). Either way exactly ONE empty node (the deleted catalog's
        // duplicate empty is gone).
        <Typography color="text.secondary">
          {registrationOpen
            ? "No events open to register for yet."
            : "Events to be announced."}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {eligibilityQueryFailed || someEligibilityUnavailable ? (
            <Alert
              severity="error"
              role="alert"
              action={
                <Button
                  type="button"
                  color="inherit"
                  size="small"
                  onClick={() => void eligibilityQ.refetch()}
                >
                  Retry
                </Button>
              }
            >
              {eligibilityQueryFailed
                ? "We couldn't confirm your eligibility, so registration is unavailable right now."
                : "Some events are missing eligibility details, so registration is unavailable only for those events."}
            </Alert>
          ) : null}
          <List disablePadding role="list">
            {visibleEvents.map((event) => {
              const solo = soloInfoByEventId.get(event.event_id);
              const verdict = verdictByEventId.get(event.event_id);
              const registrationAccess = !user
                ? "sign_in"
                : eligibilityQueryFailed
                  ? "unavailable"
                  : verdict
                    ? "ready"
                    : "unavailable";
              return (
                <TournamentEventRow
                  key={event.event_id}
                  event={event}
                  verdict={verdict}
                  myEntry={
                    selectedEntryId
                      ? selectedEntry
                      : entryByEventId.get(event.event_id)
                  }
                  requiresPartner={solo?.requiresPartner}
                  soloDrawId={solo?.soloDrawId}
                  tournamentId={tournamentId}
                  registrationOpen={registrationOpen}
                  registrationAccess={registrationAccess}
                  onRegister={() => {
                    if (!user) {
                      signInToRegister();
                      return;
                    }
                    if (eligibilityQueryFailed || !verdict) return;
                    setRegisterMode("register");
                    setRegisterEventId(event.event_id);
                  }}
                  onWithdraw={(entry) => setWithdrawEntry(entry)}
                  onEnterSolo={() => {
                    if (!user) {
                      signInToRegister();
                      return;
                    }
                    if (eligibilityQueryFailed || !verdict) return;
                    setRegisterMode("solo");
                    setRegisterEventId(event.event_id);
                  }}
                  onSignIn={signInToRegister}
                  onFindPartner={(entry) => setFinderDraw(entry)}
                  catalog={catalogByEventId.get(event.event_id)}
                />
              );
            })}
          </List>
          {catalog && !selectedEntryId ? (
            <Typography color="text.secondary" data-testid="event-catalog-footer" sx={{ fontSize: "0.8125rem" }}>
              {catalog.footer_line}
            </Typography>
          ) : null}
        </Stack>
      )}

      {/* FALLBACK — active entries that don't map to a visible event row (the
          draws/registrations fetches disagree, or the event isn't in the
          catalog). Without this the player's held/paid place would vanish with no
          way to withdraw it. Shown by the entry's HYDRATED display names
          (event_name ?? draw_name, em-dash fallback) — never a uuid — each with a
          Withdraw control on the same #1402 refund path. */}
      {!isLoading && !registrationsQ.isError && visibleOrphans.length > 0 ? (
        <Box component="section" aria-label="Your other entries" sx={{ mt: 3 }}>
          <Typography component="h3" variant="h6" sx={{ mb: 1.5 }}>
            Your other entries
          </Typography>
          <List disablePadding component={Paper} variant="outlined" role="list">
            {visibleOrphans.map((orphan) => (
              <OrphanEntryRow
                key={orphan.entry_id}
                entry={orphan}
                onWithdraw={() => setWithdrawEntry(orphan)}
              />
            ))}
          </List>
        </Box>
      ) : null}

      {/* Per-event register dialog (free + paid). Only mounts when an event is
          chosen AND a facility is known (payments are facility-scoped). */}
      {registerEvent && facilityId ? (
        <TournamentRegisterDialog
          tournamentId={tournamentId}
          facilityId={facilityId}
          event={registerEvent}
          draws={drawsForEvent.get(registerEvent.event_id) ?? []}
          registrationQuote={
            verdictByEventId.get(registerEvent.event_id)?.registration_quote
          }
          myUid={myUid}
          isMinor={isMinor}
          legalDocuments={legalDocuments}
          legalNotes={legalNotes}
          requiresForeignDeclaration={requiresForeignDeclaration}
          mode={registerMode}
          onEnterSolo={() => setRegisterMode("solo")}
          onRegistered={() => setRegisterEventId(null)}
          onClose={() => {
            setRegisterEventId(null);
            // Accepting a waiver updates its own status cache. Re-read the
            // backend verdict before presenting another event action.
            void eligibilityQ.refetch();
          }}
        />
      ) : null}

      {/* Per-event withdraw confirm (the #1402 refund path), reusing the existing
          dialog + mutation. */}
      {withdrawEntry ? (
        <TournamentWithdrawDialog
          entry={withdrawEntry}
          refundPolicySummary={refundPolicySummary}
          onConfirm={handleWithdrawConfirm}
          onClose={handleWithdrawClose}
          withdrawPending={withdraw.isPending}
          withdrawError={withdraw.error}
        />
      ) : null}

      {/* Partner finder (SLICE 1.4-c) — opened from a `partner_wanted` row's
          "Find a partner", scoped to that entry's draw. */}
      {finderDraw ? (
        <PartnerFinderDialog
          drawId={finderDraw.draw_id}
          tournamentId={tournamentId}
          drawName={finderDraw.event_name ?? finderDraw.draw_name ?? undefined}
          onClose={() => setFinderDraw(null)}
        />
      ) : null}
    </Box>
  );
}

/**
 * Prefer an ACTIVE entry over a not-entered (withdrawn/rejected/cancelled) one when
 * an event somehow has more than one entry across its draws. Mirrors the row's
 * INACTIVE set so the list and the row agree on what "still entered" means.
 */
const INACTIVE_ENTRY_STATUSES = new Set([
  "withdrawn",
  "rejected",
  "cancelled",
  "declined",
]);

function preferEntry(
  candidate: TournamentPlayerEntryResponse,
  existing: TournamentPlayerEntryResponse,
): boolean {
  const candidateActive = !INACTIVE_ENTRY_STATUSES.has(candidate.status);
  const existingActive = !INACTIVE_ENTRY_STATUSES.has(existing.status);
  // An active candidate beats an inactive existing; otherwise keep the existing.
  return candidateActive && !existingActive;
}

// ---------------------------------------------------------------------------
// Fallback row for an ACTIVE entry that maps to no visible event (orphan). Shown
// by the entry's HYDRATED display name (event_name ?? draw_name ?? em-dash) —
// never a uuid — with a humanized status + a Withdraw control on the same #1402
// refund path. The id rides the callback, never the DOM text.
// ---------------------------------------------------------------------------

function OrphanEntryRow({
  entry,
  onWithdraw,
}: {
  entry: TournamentPlayerEntryResponse;
  onWithdraw: () => void;
}) {
  const displayName = entry.event_name ?? entry.draw_name ?? "—";
  return (
    <ListItem
      divider
      data-entry-id={entry.entry_id}
      sx={{ gap: 2, justifyContent: "space-between" }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800 }}>{displayName}</Typography>
        <Chip
          size="small"
          role="status"
          label={humanizeEntryStatus(entry.status)}
          sx={{ mt: 0.75 }}
        />
      </Box>
      <Button
        type="button"
        size="small"
        variant="outlined"
        onClick={onWithdraw}
      >
        Withdraw
      </Button>
    </ListItem>
  );
}
